import { ethers } from 'ethers'

import { bracketSizeFor, manualGroupStructure, type GroupStructureMode } from './groupStructure'
import type {
  Contributor,
  CreateTournamentInput,
  DraftSettings,
  PaymentMethod,
  PrizePosition,
  RegionSettings,
  TeamFormation,
} from './types'

/**
 * Pure payload builders for the Create tournament flow.
 *
 * Every payment path used to copy-paste the same draft / group-play / contributor
 * blocks. These helpers produce the exact same objects (same keys, same insertion
 * order) as the old inline code, plus the intended `team_formation`,
 * `draft_settings` and `region_settings` additions for solo-signup formats.
 */

/** Which submit path is building the payload (mirrors the old onSubmit branches). */
export type PaymentBranch =
  | 'free'
  | 'paypal'
  | 'coinbase_usdc'
  | 'leet_token_test_mode'
  | 'leet_token_deposit'

export type GroupPlayUiConfig = {
  /** 'auto' = backend picks groups from the teams that sign up (unchanged behavior). */
  structureMode: GroupStructureMode
  /** Manual only: the organizer's number of groups. */
  numberOfGroups?: number
  /** Manual only: teams advancing from each group to the bracket. */
  advancePerGroup?: number
  /** Auto only: bubble teams play a best-of-3 for the last bracket spots. */
  playInEnabled: boolean
  grandFinalsFormat: 'bo3' | 'bo5'
}

export const DEFAULT_GROUP_PLAY: GroupPlayUiConfig = { structureMode: 'auto', playInEnabled: true, grandFinalsFormat: 'bo3' }

export type FormationState = {
  teamFormation: TeamFormation
  /** The bracket type picked in the Format step (same value as the form's play_format). */
  selectedFormat: string
  draftSettings: DraftSettings
  regionSettings: RegionSettings
  isGroupPlayEnabled: boolean
  groupPlayConfig: GroupPlayUiConfig
}

export type CommunityContext = { communityId: string; communityName?: string } | null | undefined

export type PositionPayload = { prize: string; percentage: number }
export type ContributorPayload = { name: string; address: string; role: string; percentage: number }

export const DEFAULT_DRAFT_SETTINGS: DraftSettings = {
  captain_selection_method: 'random',
  draft_order_type: 'snake',
}

export const DEFAULT_REGION_SETTINGS: RegionSettings = {
  balance_by_mmr: true,
  max_subs_per_team: 2,
  allow_international: true,
}

export const isSoloSignup = (formation: TeamFormation) => formation !== 'teams'

/** Remove ids and convert percentages to numbers (unchanged from the old onSubmit). */
export const toPositionsPayload = (positions: PrizePosition[]): PositionPayload[] =>
  positions.map((position) => ({
    prize: position.prize,
    percentage: parseInt(position.percentage),
  }))

export const toContributorsPayload = (contributors: Contributor[]): ContributorPayload[] => {
  if (contributors.length === 0) return []
  return contributors.map((contributor) => ({
    name: contributor.name,
    address: contributor.address,
    role: contributor.role,
    percentage: parseInt(contributor.percentage),
  }))
}

/**
 * The payment-specific fields each branch spread right after `prize_positions`.
 * Order matters for the PayPal path, which serialises keys in insertion order.
 */
const paymentFields = (branch: PaymentBranch, transactionHash?: string) => {
  switch (branch) {
    case 'free':
      return { payment_method: 'free' as PaymentMethod, transactionHash: 'FREE_TOURNAMENT_NO_TX' }
    case 'paypal':
      return { payment_method: 'paypal' as PaymentMethod }
    case 'coinbase_usdc':
      return { payment_method: 'coinbase_usdc' as PaymentMethod }
    case 'leet_token_test_mode':
      return { transactionHash: 'TEST_MODE_NO_TX', payment_method: 'leet_token' as PaymentMethod }
    case 'leet_token_deposit':
      return { transactionHash: transactionHash as string }
  }
}

/**
 * Solo-signup team size must be 2–10 (backend validator, 422 otherwise). The form
 * already blocks other values; this keeps the payload safe if one slips through
 * (e.g. the max_player_size fallback from Teams register). 0 = unset, left as-is.
 */
export const clampSoloTeamSize = (n: number) => (n > 0 ? Math.min(10, Math.max(2, Math.round(n))) : n)

/**
 * Fields for solo-signup formats (Captain draft and Region teams). Both use the
 * backend's `captain_pick` play format with the real bracket type in `bracket`,
 * exactly like draft mode always has.
 */
export const buildDraftPayload = (
  tournamentData: Pick<CreateTournamentInput, 'players_per_team' | 'max_player_size'>,
  formation: Pick<FormationState, 'teamFormation' | 'selectedFormat' | 'draftSettings' | 'regionSettings'>,
) => ({
  play_format: 'captain_pick', // Backend still uses captain_pick for draft mode
  bracket: formation.selectedFormat, // The actual bracket type (single_elimination, double_elimination…)
  max_draft_players: 999, // Effectively unlimited signups
  players_per_team: clampSoloTeamSize(
    Number(tournamentData.players_per_team) || parseInt(tournamentData.max_player_size) || 0,
  ),
  team_formation: formation.teamFormation as Exclude<TeamFormation, 'teams'>,
  ...(formation.teamFormation === 'captain_pick'
    ? { draft_settings: { ...formation.draftSettings } }
    : { region_settings: { ...formation.regionSettings } }),
})

/**
 * Group stage fields, identical on every payment path.
 *
 * Auto: `{ structureMode: 'auto', playInEnabled }` — the backend works out the
 * groups at start from the teams that actually signed up (today's behavior).
 * Manual: the organizer's exact shape. The backend honors it in every group
 * generation path (start tournament, Groups page, legacy group-play/start).
 *
 * (Before the structure controls existed the LEET deposit path sent a
 * `teamsPerGroup` key the UI never set, so it was always dropped; the real
 * config replaces that quirk on purpose.)
 */
export const buildGroupPlayPayload = (
  groupPlayConfig: GroupPlayUiConfig,
  context: { teamFormation: TeamFormation; numberOfTeams?: string },
) => ({
  groupPlayEnabled: true,
  groupPlayConfig: buildGroupPlayConfig(groupPlayConfig, context),
  matchFormats: {
    grandFinals: groupPlayConfig.grandFinalsFormat,
  },
})

export const buildGroupPlayConfig = (
  config: GroupPlayUiConfig,
  { teamFormation, numberOfTeams }: { teamFormation: TeamFormation; numberOfTeams?: string },
) => {
  if (config.structureMode !== 'manual' || !config.numberOfGroups || !config.advancePerGroup) {
    return { structureMode: 'auto' as const, playInEnabled: config.playInEnabled }
  }
  const teamCount = teamFormation === 'teams' ? parseInt(numberOfTeams || '') || 0 : 0
  return {
    structureMode: 'manual' as const,
    numberOfGroups: config.numberOfGroups,
    advancePerGroup: config.advancePerGroup,
    targetBracketSize: bracketSizeFor(config.numberOfGroups * config.advancePerGroup),
    // Empty bracket spots become first-round byes for the top seeds (no play-in).
    playInEnabled: false,
    // Reference value (largest planned group); unknown for solo signup.
    ...(teamCount > 0 ? { teamsPerGroup: Math.ceil(teamCount / config.numberOfGroups) } : {}),
  }
}

/** Why the chosen group stage can't be saved, or null. */
export const validateGroupStage = ({
  isGroupPlayEnabled,
  groupPlayConfig,
  teamFormation,
  numberOfTeams,
  playFormat,
}: {
  isGroupPlayEnabled: boolean
  groupPlayConfig: GroupPlayUiConfig
  teamFormation: TeamFormation
  numberOfTeams?: string
  playFormat?: string
}): string | null => {
  if (!isGroupPlayEnabled || groupPlayConfig.structureMode !== 'manual') return null
  if (!groupPlayConfig.numberOfGroups || !groupPlayConfig.advancePerGroup) return 'Set the number of groups and how many advance.'
  const teamCount = teamFormation === 'teams' ? parseInt(numberOfTeams || '') || null : null
  return manualGroupStructure({
    teamCount,
    numberOfGroups: groupPlayConfig.numberOfGroups,
    advancePerGroup: groupPlayConfig.advancePerGroup,
    doubleElim: playFormat === 'double_elimination',
  }).error
}

export type BuildTournamentPayloadArgs = {
  branch: PaymentBranch
  tournamentData: CreateTournamentInput
  positions: PrizePosition[]
  contributors: Contributor[]
  communityContext: CommunityContext
  formation: FormationState
  /** Only for the LEET deposit path: the deposit receipt hash. */
  transactionHash?: string
}

/** Builds the object each payment path hands to createTournament / PayPal initiation. */
export const buildTournamentPayload = ({
  branch,
  tournamentData,
  positions,
  contributors,
  communityContext,
  formation,
  transactionHash,
}: BuildTournamentPayloadArgs) => {
  let tourData: Record<string, any> = {
    ...tournamentData,
    prize_positions: toPositionsPayload(positions),
    ...paymentFields(branch, transactionHash),
    ...(communityContext && { communityId: communityContext.communityId }),
  }

  if (isSoloSignup(formation.teamFormation)) {
    tourData = { ...tourData, ...buildDraftPayload(tournamentData, formation) }
  }

  if (formation.isGroupPlayEnabled) {
    tourData = {
      ...tourData,
      ...buildGroupPlayPayload(formation.groupPlayConfig, {
        teamFormation: formation.teamFormation,
        numberOfTeams: tournamentData.number_of_teams,
      }),
    }
  }

  if (contributors.length > 0) {
    tourData = { ...tourData, contributors: toContributorsPayload(contributors) }
  }

  return tourData
}

/** Keys the PayPal initiation endpoint receives as JSON strings. */
const PAYPAL_JSON_KEYS = ['prize_positions', 'contributors', 'draft_settings', 'region_settings', 'groupPlayConfig', 'matchFormats']

/**
 * FormData for POST /tournament/payment/initiate (PayPal path; does not go through
 * the createTournament thunk). Unchanged except that the draft/region settings and
 * group play objects are JSON encoded instead of becoming "[object Object]"
 * (which the backend could not store, so PayPal group-play tournaments failed
 * after payment).
 */
export const buildPaypalFormData = (tourData: Record<string, any>): FormData => {
  const formData = new FormData()
  Object.keys(tourData).forEach((key) => {
    const value = tourData[key]
    if (value !== undefined && value !== null) {
      if (PAYPAL_JSON_KEYS.includes(key)) {
        formData.append(key, JSON.stringify(value))
      } else if (key === 'avatar' || key === 'cover_photo') {
        if (value instanceof File) {
          formData.append(key, value)
        }
      } else {
        formData.append(key, String(value))
      }
    }
  })
  return formData
}

/**
 * The prize checks onSubmit has always run, in the same order and with the same
 * messages. Returns the first problem, or null. The USDC "> 0" check lives in its
 * own branch in onSubmit; pass includeUsdc to also run it (used by step validation).
 */
export const validatePrizeSetup = ({
  paymentMethod,
  prizePool,
  positions,
  contributors,
  includeUsdc = false,
}: {
  paymentMethod: PaymentMethod
  prizePool: string | undefined
  positions: PrizePosition[]
  contributors: Contributor[]
  includeUsdc?: boolean
}): string | null => {
  const pool = parseFloat(prizePool || '0')

  if (paymentMethod === 'free') {
    if (pool !== 0) return 'Free tournaments can’t have a prize pool. Set it to 0 or pick another payment method.'
  } else if (paymentMethod === 'paypal') {
    if (pool <= 0) return 'Enter a prize pool above $0 for a PayPal tournament.'
  } else if (paymentMethod === 'leet_token') {
    if (pool <= 0) return 'Enter a prize pool above 0 LEET, or choose Free.'
  } else if (includeUsdc && paymentMethod === 'coinbase_usdc') {
    if (!(pool > 0)) return 'Enter a prize pool above 0 USDC, or choose Free.'
  }

  const firstPrize = positions.find((position) => position.id === '1')
  if (!firstPrize?.percentage) return 'Enter a percentage for 1st place.'

  const emptyPosition = positions.find(
    (position) => position.percentage === '' || position.percentage == '0',
  )
  if (emptyPosition) return 'Enter a percentage for every prize position, or remove the empty ones.'

  if (contributors.length > 0) {
    const emptyContributor = contributors.find(
      (contributor) =>
        contributor.name === '' ||
        contributor.address === '' ||
        contributor.role === '' ||
        contributor.percentage === '',
    )
    if (emptyContributor) return 'Fill in the name, wallet, role and share for every content creator.'

    const invalidAddresses = contributors.filter((contributor) => !ethers.isAddress(contributor.address))
    if (invalidAddresses.length > 0) {
      return `Check the wallet address${invalidAddresses.length > 1 ? 'es' : ''}: ${invalidAddresses
        .map((c) => c.address || 'empty')
        .join(', ')}`
    }
  }

  const sum = positions.reduce((acc, curr) => acc + parseInt(curr.percentage), 0)
  const contributorsSum = contributors.reduce((acc, curr) => acc + parseInt(curr.percentage), 0)
  const total = sum + contributorsSum
  if (total !== 100) return `Prize places and creator shares must add up to 100% (currently ${total}%).`

  return null
}
